import { css } from '@emotion/core'
import { lighten } from 'polished'

import './fonts/typeface-montserrat'
import './fonts/typeface-poppins'
const name = 'business.com'
const SITE_NAME = 'bdc'

/**
 * Stole this Breakpoint setting logic from BuyerZone (@Alex + @Steve)
 */

// made BREAKPOINTS an object to easily use as a constant within main code
const BREAKPOINTS = {
	XS: 380,
	S: 569,
	M: 768,
	L: 992,
	XL: 1200,
	MAX: 1366,
	MMAX: 1800,
}

const LIST_BREAKPOINTS = Object.entries(BREAKPOINTS) // make breakpoints into list

// sample return size val => (min-width: 768px)
const MATCH_MEDIA = LIST_BREAKPOINTS.reduce(
	(acc, bp) => ({
		...acc,
		[bp[0]]: `(min-width: ${bp[1]}px)`,
	}),
	{}
)

// sample return size val => @media (min-width: 768px)
const MQ = LIST_BREAKPOINTS.reduce(
	(acc, bp) => ({
		...acc,
		[bp[0]]: `@media ${MATCH_MEDIA[bp[0]]}`,
	}),
	{}
)

/* MEDIA QUERY FOR BETWEEN BREAKPOINTS */
const BETWEEN_BREAKPOINTS = [
	['SM', 569, 768],
	['ML', 768, 992],
	['MXL', 768, 1200],
	['LXL', 992, 1200],
]

// sample return size val => (min-width: 576px) and (max-width: 768px)
const MATCH_BETWEEN_MEDIA = BETWEEN_BREAKPOINTS.reduce(
	(acc, bp) => ({
		...acc,
		[bp[0]]: `(min-width: ${bp[1]}px) and (max-width: ${bp[2]}px)`,
	}),
	{}
)

// sample return size val => @media (min-width: 768px)
const BMQ = BETWEEN_BREAKPOINTS.reduce(
	(acc, bp) => ({
		...acc,
		[bp[0]]: `@media ${MATCH_BETWEEN_MEDIA[bp[0]]}`,
	}),
	{}
)

/* HEADERS */
const HEADERS = [
	['h1', 55, 38],
	['h2', 30, 24],
	['h3', 22, 18],
]

const header = HEADERS.reduce(
	(acc, header) => ({
		...acc,
		[header[0]]: `
		font-size: ${header[2]}px;
		font-weight: 500;
		${MQ.M} {
			font-size: ${header[1]}px;
		}
	`,
	}),
	{}
)

/* Max Widths */

const MAXWIDTH = [
	['L', 1200], // Ultimate max for displaying text
]

const maxWidth = MAXWIDTH.reduce(
	(acc, max) => ({
		...acc,
		[max[0]]: `
		max-width: ${max[1]}px;
	`,
	}),
	{}
)

/* FONTS */
const fonts = {
	primary: css`
		font-family: 'Poppins', 'sans-serif';
	`,
	//CTA
	secondary: css`
		font-family: 'Montserrat SemiBold', 'Adobe Blank', sans-serif;
	`,
	header: css`
		font-family: 'Poppins Medium', 'sans-serif';
	`,
	bold: css`
		font-family: 'Poppins Bold', sans-serif;
		font-weight: 700;
	`,

	// NEW THEMING STRUCTURE THAT WILL EVENTUALLY TAKE OVER THE ABOVE
	general: css`
		font-family: 'Poppins', 'sans-serif';
		font-size: 16px;

		${MQ.M} {
			font-size: 18px;
		}
	`,
	body: css`
		font-family: 'Poppins', 'sans-serif';
		font-size: 16px;
	`,
	primarySemiBold: css`
		font-family: 'Poppins SemiBold', 'sans-serif';
	`,
	primaryMedium: css`
		font-family: 'Poppins Medium', sans-serif;
	`,
	secondaryExtraBold: css`
		font-family: 'Montserrat ExtraBold';
	`,
	primaryBlack: css`
		font-family: 'Poppins Black', 'sans-serif';
	`,
}

const colors = {
	black: '#000000',
	blue: '#ababab',
	// blueGrey: '#D8DFE4',
	// blueLagoon: '#005564',
	buttonText: '#044A58',
	contentLink: '#087EE2',
	// contentLinkHover: '#FFA06A',
	// darkBlue: '#00348D',
	// darkGreen: '#008D64',
	darkGrey: '#505050', // old hex: #787878
	// darkGreyOld: '#787879',
	dimGrey: '#6d6d6d',
	// dolphin: '#6b6a7a',
	grey01: '#E1E1E1',
	// grey00: '#E0E0E0',
	green: '#A1D884',
	lightBlue: '#094463',
	// lightBrown: '#d3d3d3',
	lightGrey: '#F2F5F7',
	lime: '#E0E721',
	navy: '#003B5C',
	orange: '#FFA06A',
	outlineBlue: '#0d8ed1' /** Change to action.focus */,
	purple: '#44499C',
	red: '#ff4136',
	teal: '#00788D',
	// darkTeal: '#00263B',
	text: '#49485B',
	washedNavy: '#768DA7' /* SEE CHANGE BELOW */,

	// THESE ARE BASED OFF OF NEW REVISED GREY COLOR SCHEME PROVIDED BY KEVIN
	white: '#ffffff',
	// greyL1: '#F2F5F7',
	// greyM1: '#919191',
	// greyM2: '#505050',
	gray: {
		50: '#F2F5F7',
		100: '#FAFAFA',
		200: '#EEEEEE',
		300: '#D7D7D7',
		400: '#B0B0B0',
		500: '#7A7A7A',
		600: '#505050',
		700: '#2D2D2D',
		800: '#181818',
		900: '#000000',
	},

	// NEW THEMING STRUCTURE THAT WILL EVENTUALLY TAKE OVER THE ABOVE
	primary: '#003B5C',
	secondary: '#FFA06A',
	tertiary: '#00788D',
	tertiaryLight: lighten(0.09, '#00788D'),
	general: '#505050',
}

const navigation = {
	linkColor: colors.primary,
	linkHoverColor: colors.secondary,
	altLinkColor: colors.tertiary,
	altLinkHoverColor: colors.secondary,
}

const navigationBar = {
	backgroundColor: colors.tertiary,
}

const backToTop = {
	mainColor: colors.secondary,
	hoverColor: colors.tertiary,
}

/** Remove */
const badges = {
	article: '#00348D',
	category: colors.tertiary,
	editorsPick: colors.tertiary,
	question: '#008D64',
	review: colors.tertiary,
}

const bestPicks = {
	badgeWrapperBackground: colors.primary,
	badgeWrapperFeaturedTextColor: colors.buttonText,
	bannerRibbonColor1: '#FFB78E' /* use palette light and dark versions */,
	bannerRibbonColor2: colors.secondary,
	bannerRibbonColor3: '#FF8945',
	bannerRibbonColor4: '#FFC3A1',
	bulletTextColor: colors.primary,
	checkMark: colors.tertiary,
	chevronColor: colors.secondary,
	companyNameColor: colors.tertiary,
	companyNameHoverColor: colors.secondary,
	featuredTitleBackground: colors.primary,
	featuredTitleColor: colors.secondary,
	meatballColor: colors.secondary,
	moreCoverageArticleBackground: 'rgba(255, 255, 255, 0.14)',
	moreCoverageTitleColor: colors.secondary,
	ourApproachBackground: css`
		background: ${colors.primary};
	`,
	primarySvgColor: '#6AABB8',
	secondarySvgColor: colors.secondary,
	altSectionTitleColor: colors.tertiary,
	sectionTitleColor: colors.primary,
	serviceTitleColor: colors.primary,
}

const editorialUpdateSection = {
	mainColor: colors.tertiary,
}

const hubTemplate = {
	articleTitle: colors.primary,
	articleTitleHover: colors.secondary,
	bestPickTitleColor: colors.primary,
	bestPickTitleHoverColor: colors.secondary,
	editorsBlockTitle: colors.primary,
	hoverPlusArrowColor: colors.secondary,
	linkColor: colors.secondary,
	linkHoverColor: colors.tertiary,
	top3BestPickBackground: 'rgba(0, 121, 141, 0.75)',
}

const simpleHeader = {
	breadcrumbsBackground: colors.gray[50],
	authorByLineColor: 'rgba(255, 255, 255, 0.6)',
	hubTitleColor: colors.white,
	mbpTitleColor: colors.white,
	fontFamily: fonts.header,
	hubGradientBgLight: '#008F9D',
	hubGradientBgDark: colors.tertiary,
	mbpGradientBgLight: '#008F9D',
	mbpGradientBgDark: colors.tertiary,
}

const search = {
	borderColor: colors.secondary,
	strokeColor: colors.primary,
}

const footer = {
	chevronColor: colors.secondary,
}

const userNavDropdown = {
	secondaryBackground: '#084463',
}

// NEW THEMING STRUCTURE THAT WILL EVENTUALLY TAKE OVER BUTTONS
const button = {
	fontFamily: 'Montserrat ExtraBold',
	primary: {
		buttonColor: colors.secondary,
		buttonText: colors.buttonText,
		hoverColor: colors.tertiary,
		hoverBorderColor: colors.tertiary,
		hoverTextColor: colors.white,
	},
	secondary: {
		borderColor: colors.secondary,
		buttonColor: colors.white,
		buttonText: colors.buttonText,
		hoverColor: colors.tertiary,
		hoverBorderColor: colors.tertiary,
		hoverTextColor: colors.white,
	},
	tertiary: {
		borderColor: colors.green,
		buttonColor: colors.green, //replace "green"
		buttonText: colors.buttonText,
		hoverColor: colors.primary,
		hoverBorderColor: colors.primary,
		hoverTextColor: colors.white,
	},
	warning: {
		borderColor: '#ec3a3a', //replace
		buttonColor: '#ec3a3a',
		buttonText: colors.white,
		hoverColor: colors.primary,
	},
}

const arrowActionCard = {
	arrowColor: colors.secondary,
	topBannerBackground: colors.tertiary,
	topBannerHover: colors.primary,
}

const authorBio = {
	staffName: colors.primary,
	staffSite: colors.primary,
	staffProfileLink: colors.secondary,
	staffProfileLinkHover: colors.tertiary,
	altStaffProfileLinkHover: colors.secondary,
}

const contentHeader = {
	authorBylineBorder: colors.tertiary,
}

const emailCapture = {
	buttonColor: colors.secondary,
	floaterBackground: colors.tertiary,
	inlineButtonHover: colors.primary,
	labelColor: colors.primary,
	policyLink: colors.secondary,
	policyLinkHover: colors.purple,
}

const relatedRail = {
	backgroundHoverColor: colors.gray[100],
	headerFontFamily: fonts.primarySemiBold,
	linkColor: colors.tertiary,
	linkHoverColor: colors.secondary,
	textColor: colors.gray[600],
}

const reviews = {
	border: colors.gray[400],
	categoryLink: colors.tertiary,
	categoryHoverLink: colors.secondary,
}

const sectionTitle = {
	textColor: colors.primary,
}

const socialProfileSvgs = {
	primary: {
		backgroundColor: 'none',
		fillColor: colors.secondary,
		hoverBackgroundColor: 'none',
		hoverFillColor: colors.tertiary,
	},
	neutral: {
		backgroundColor: colors.gray[400],
		fillColor: colors.white,
		hoverBackgroundColor: colors.secondary,
		hoverFillColor: colors.white,
	},
	inverse: {
		backgroundColor: 'none',
		fillColor: colors.secondary,
		hoverBackgroundColor: 'none',
		hoverFillColor: colors.tertiary,
	},
}

const tableOfContents = {
	bulletBorderColor: colors.primary,
	hoverColor: colors.secondary,
	lineColor: colors.washedNavy /* make this palette.primary.light */,
}

const background = {
	default: {
		backgroundColor: colors.white,
	},
	secondary: {
		backgroundColor: 'rgba(0, 0, 0, 0.12)',
	},
}

const shadows = {
	small: '0px 0px 2px rgba(0, 0, 0, 0.16)',
	medium: '0 2px 30px 0 rgba(0, 0, 0, 0.16)',
	large: '0px 3px 3px rgba(0, 0, 0, 0.3)',
	greyMedium: '0px 2px 14px 0px rgba(139, 142, 145, 0.27)',
}

const subHubMenu = {
	buttonVariant: 'primary',
	linkVariant: 'primary',
}

/* STYLED TEXT */
const text = {
	bodyLarge: css`
		font-size: 18px;
	`,
	bodySmall: css`
		font-size: 14px;

		${MQ.L} {
			font-size: 16px;
		}
	`,
	display1: css`
		font-family: Poppins;
		font-size: 25px;
		font-weight: 500;
		font-style: normal;
		font-stretch: normal;
		line-height: normal;
		letter-spacing: 0.6px;
		color: ${colors.white};
		margin: 30px 0 0 0;
		height: 42px;
	`,
	display2: css`
		padding-top: 48px;
		margin: 0 48px;
		font-family: Poppins;
		font-size: 1.2rem;
		font-weight: 500;
		font-style: normal;
		font-stretch: normal;
		line-height: normal;
		letter-spacing: 0.4px;
	`,
	menuItem: css`
		color: ${colors.primary};
		font-size: 20px;
	`,
	subMenuItem: css`
		color: ${colors.primary};
		font-size: 16px;
	`,
	sectionHeaderLarge: css`
		color: ${colors.tertiary};
		font-size: 16px;
		font-weight: 500;
		${fonts.secondary};
		letter-spacing: 3px;
		text-transform: uppercase;

		${MQ.M} {
			font-size: 22px;
		}
	`,
	sectionHeaderSmall: css`
		color: ${colors.tertiary};
		font-size: 14px;
		font-weight: 500;
		${fonts.secondary};
		letter-spacing: 3px;
		text-transform: uppercase;
	`,
}

const borders = {
	default: `1px solid ${colors.grey01}`,
	secondary: `1px solid rgba(109, 109, 109, 0.2)`,
}

/* CTA LINKS */
const baseCta = css`
	cursor: pointer;
	font-size: 14px;
	font-weight: 700;
	letter-spacing: 3px;
`

const ctas = {
	default: css`
        ${baseCta}
        color: ${colors.secondary};
        &:hover {
            color: ${colors.tertiary};
        }
	`,
}

/* LINKS */
const baseLink = css`
	cursor: pointer;
`

const links = {
	default: css`
        ${baseLink}
        color: ${colors.secondary};
        &:hover {
            color: ${colors.tertiary};
        }
    `,
	white: css`
		${baseLink}
		color: ${colors.white};
		&:hover {
			color: ${colors.secondary};
		}
	`,
	darkGrey: css`
		${baseLink}
		color: ${colors.darkGrey};
		&:hover {
			color: ${colors.secondary};
		}
	`,
}

const shareBaseButton = css`
	border: none;
	display: block;
	cursor: pointer;
`

/* BUTTONS */
const baseButton = css`
	${shareBaseButton}
	font-size: 14px;
	font-weight: 800;
	letter-spacing: 3px;
	max-width: 290px;
	padding: 20px 0;
	text-align: center;
	text-transform: uppercase;
	transition: all 0.4s ease;
	width: 100%;
	${MQ.L} {
		max-width: 288px;
		padding: 21px 0;
	}

	&:disabled {
		background-color: ${colors.gray[300]};
		cursor: not-allowed;
		transition: none;
	}
`

const smallBaseButton = css`
	${shareBaseButton}
	${fonts.secondary};
	font-size: 12px;
	font-weight: 800;
	letter-spacing: 2px;
	max-width: fit-content;
	padding: 13px 10px;
	text-align: center;
	text-transform: uppercase;
	transition: all 0.4s ease;
	width: 100%;
`

/* BUTTONS */
const buttons = {
	default: css`
		${baseButton}
		background: ${colors.secondary};
		color: ${colors.buttonText};
		${fonts.secondary};
		&:hover:not([disabled])  {
			background: ${colors.tertiary};
			color: ${colors.white};
		}
		&:active {
			background-color: ${colors.blueLagoon};
		}
	`,
	transparent: css`
		${baseButton}
		outline: solid 1px ${colors.blueGrey};
		color: ${colors.black};
		&:hover:not([disabled]) {
			background-color: ${colors.secondary};
			color: ${colors.white};
		}
		&:active {
			background-color: ${colors.blueLagoon};
		}
	`,
	link: css`
		${baseButton}
		${baseCta}
		background: transparent;
		color: ${colors.secondary};
		${fonts.secondary};
		font-weight: 600;
		font-size: 1rem;
		text-transform: none;
		width: 80%;
	`,
	small: css`
		${smallBaseButton}
		background: ${colors.secondary};
		color: ${colors.buttonText};
		${fonts.secondary};
		&:hover {
			background: ${colors.tertiary};
			color: ${colors.white};
		}
	`,
	smallSecondary: css`
		${smallBaseButton}
		background: ${colors.green};
		color: ${colors.buttonText};
		width: auto;
		padding: 20px 15px;
		&:hover {
			background: ${colors.primary};
			color: ${colors.white};
		}
	`,
}

export const buttonVariantKeys = Object.keys(buttons)

const triangle = css`
	width: 0;
	height: 0;
	border-left: 7px solid transparent;
	border-right: 7px solid transparent;
	border-bottom: 11px solid ${colors.secondary};
`
export const bdcTheme = {
	arrowActionCard,
	authorBio,
	background,
	backToTop,
	badges,
	bestPicks,
	BMQ,
	borders,
	BREAKPOINTS,
	button,
	buttons,
	buttonVariantKeys,
	colors,
	contentHeader,
	ctas,
	editorialUpdateSection,
	emailCapture,
	fonts,
	footer,
	header,
	hubTemplate,
	links,
	MATCH_BETWEEN_MEDIA,
	MATCH_MEDIA,
	maxWidth,
	MQ,
	name,
	navigation,
	navigationBar,
	relatedRail,
	reviews,
	search,
	sectionTitle,
	shadows,
	simpleHeader,
	SITE_NAME,
	socialProfileSvgs,
	subHubMenu,
	tableOfContents,
	text,
	triangle,
	userNavDropdown,
}
